import { useLayoutEffect, useEffect, useRef, useState, useCallback } from 'react';

interface UseOverflowChipsResult {
  visibleCount: number;
  hiddenCount: number;
}

const DEBOUNCE_DELAY = 100;
const OVERFLOW_CHIP_WIDTH = 80;

/**
 * Calculates how many pills are visible vs hidden based on available width.
 * Measures pill widths from a hidden measurement container that always
 * renders ALL pills, avoiding the chicken-and-egg measurement problem.
 *
 * @param itemCount Total number of items
 * @param searchBarRef Ref to the search bar inner flex container (for available width)
 * @param measureRef Ref to a hidden container that renders all pills for measurement
 * @param reservedWidth Fixed pixels reserved for SearchInput + clear button + padding + gaps
 */
export const useOverflowChips = (
  itemCount: number,
  searchBarRef: React.RefObject<HTMLElement>,
  measureRef: React.RefObject<HTMLElement>,
  reservedWidth: number
): UseOverflowChipsResult => {
  const [visibleCount, setVisibleCount] = useState(itemCount);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculate = useCallback((): void => {
    if (itemCount === 0) {
      setVisibleCount(itemCount);
      return;
    }

    const searchBarEl = searchBarRef.current;
    const measureEl = measureRef.current;
    if (!searchBarEl || !measureEl) {
      setVisibleCount(itemCount);
      return;
    }

    const children = Array.from(measureEl.children) as HTMLElement[];
    if (children.length !== itemCount) {
      setVisibleCount(itemCount);
      return;
    }

    const availableWidth = searchBarEl.clientWidth - reservedWidth;
    if (availableWidth <= 0) {
      setVisibleCount(0);
      return;
    }

    // Check if all pills fit
    const baseX = children[0].getBoundingClientRect().left;
    const lastChild = children[children.length - 1];
    const totalPillsWidth = lastChild.getBoundingClientRect().right - baseX;

    if (totalPillsWidth <= availableWidth) {
      setVisibleCount(itemCount);
      return;
    }

    // Find how many fit, reserving space for the "+N more" chip
    let count = 0;
    for (let i = 0; i < children.length; i += 1) {
      const childEnd = children[i].getBoundingClientRect().right - baseX;
      const isLastItem = i === children.length - 1;
      const overflowSpace = isLastItem ? 0 : OVERFLOW_CHIP_WIDTH;

      if (childEnd + overflowSpace <= availableWidth) {
        count = i + 1;
      } else {
        break;
      }
    }

    setVisibleCount(count);
  }, [itemCount, searchBarRef, measureRef, reservedWidth]);

  useLayoutEffect(() => {
    calculate();
  }, [calculate]);

  useEffect(() => {
    const searchBarEl = searchBarRef.current;
    if (!searchBarEl) return;

    const onResize = (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(calculate, DEBOUNCE_DELAY);
    };

    const observer = new ResizeObserver(onResize);
    observer.observe(searchBarEl);

    return () => {
      observer.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchBarRef, calculate]);

  return {
    visibleCount,
    hiddenCount: itemCount - visibleCount,
  };
};
